import LegalLayout, { Section } from "@/components/legal/LegalLayout";
import { usePublicSettings } from "@/hooks/usePublicSettings";
import usePageMeta from "@/hooks/usePageMeta";

export default function Refunds() {
  const { settings } = usePublicSettings();
  const supportEmail = settings.CONTACT_EMAIL_SUPPORT || "support@colourmatch.au";
  usePageMeta({
    title: "Refund Policy — ColourMatch AI Paint Visualiser",
    description: "ColourMatch (AI paint visualiser software for Australian homeowners) refund policy, including your statutory rights under the Australian Consumer Law.",
    path: "/refunds",
  });
  return (
    <LegalLayout
      title="Refund Policy"
      lastUpdated="February 2026"
      testid="refunds-page"
      intro={
        <span><strong>Draft for review.</strong> Australian Consumer Law cannot be excluded — please have this reviewed by an Australian lawyer before live trading.</span>
      }
    >
      <Section heading="1. Australian Consumer Law">
        <p>
          You have rights under the Australian Consumer Law (Schedule 2 of the Competition and
          Consumer Act 2010 (Cth)) that cannot be excluded, including the right to a refund,
          repair or replacement if a service is not delivered with due care and skill or is
          not fit for purpose. This policy supplements (and does not override) those rights.
        </p>
      </Section>

      <Section heading="2. What you are paying for">
        <p>
          The $29.99 AUD unlock fee gives you access to a digital, AI-generated render of your
          uploaded photo with your chosen paint colour applied, plus the ability to test
          additional colours, save the project, use the before/after slider and download an HD
          watermark-free image. The AI render is generated on demand and the result is
          delivered to you immediately.
        </p>
      </Section>

      <Section heading="3. When you can request a refund">
        <p>Subject to Australian Consumer Law, we will refund the unlock fee if:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>The AI render fails to generate and we cannot resolve the issue within 48 hours; or</li>
          <li>The rendered image is materially defective (e.g. recolours an entirely wrong surface, blanks the image, or is unusable); or</li>
          <li>You were charged due to a technical error or duplicate transaction.</li>
        </ul>
      </Section>

      <Section heading="4. When refunds may not apply">
        <p>Because the unlock fee delivers an immediate digital service, refunds may not apply where:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>You have downloaded HD copies of the render(s); or</li>
          <li>You simply changed your mind about the colour you chose; or</li>
          <li>Screen rendering of the colour differs slightly from the physical paint (a known limitation of digital displays).</li>
        </ul>
        <p>This does not affect any non-excludable rights you have under Australian Consumer Law.</p>
      </Section>

      <Section heading="5. How to request a refund">
        <p>
          Email <strong>{supportEmail}</strong> within <strong>14 days</strong> of your purchase
          with:
        </p>
        <ul className="list-disc pl-5 space-y-1">
          <li>The email address used at purchase;</li>
          <li>The project title;</li>
          <li>A brief description of the issue, ideally with screenshots.</li>
        </ul>
        <p>
          We aim to respond within 2 business days. Approved refunds are processed via Stripe
          and typically appear on your card within 5–10 business days.
        </p>
      </Section>

      <Section heading="6. Disputes">
        <p>
          If we cannot resolve your concern, you may contact the Australian Competition and
          Consumer Commission (ACCC) at <a className="underline" href="https://www.accc.gov.au" target="_blank" rel="noreferrer">accc.gov.au</a>, or your state/territory consumer protection agency.
        </p>
      </Section>

      <Section heading="7. Contact">
        <p>Refund enquiries: <strong>{supportEmail}</strong></p>
      </Section>
    </LegalLayout>
  );
}
